import { useCallback, useEffect, useState } from 'react';

import { useAuth } from '@auth/AuthProvider';
import TrainingController from '@data/controller/TrainingController';
import { TrainingModule } from '@interfaces/training.interface';

// `enabled: false` holds off fetching, for components that stay mounted while
// hidden and only need the list once they're shown.
export default function useTrainingModules(options: { enabled?: boolean } = {}) {
  const enabled = options.enabled ?? true;
  const auth = useAuth();
  const churchID = auth.churchId;

  const [modules, setModules] = useState<TrainingModule[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!churchID) return;

    setLoading(true);
    setError(null);

    const response = await TrainingController.fetchTrainingModules(churchID, auth);

    if (response.error || !response.body) {
      setError(response.error || 'Failed to load Training Modules.');
      setModules([]);
    } else {
      setModules(response.body.results ?? []);
    }

    setLoading(false);
    setHasLoaded(true);
  }, [churchID, auth]);

  useEffect(() => {
    if (!enabled) return;

    void fetch();
  }, [fetch, enabled]);

  return { modules, loading, hasLoaded, error, refetch: fetch } as const;
}
